import { useIsMutating, useMutation } from "@tanstack/react-query";
import { pathPickQueryApi } from "@/features/pathpick/api/query";
import { PathPickProductsResponse } from "@/features/pathpick/types/response";
import { PathPickProduct } from "@/features/pathpick/types";
import { BASKET_BULK_ORDER_LIMIT } from "@/features/pathpick/features/basket/constants";
import usePathPickBasketSelection from "@/features/pathpick/features/basket/hooks/usePathPickBasketSelection";
import useCompanyIdentity from "@/shared/hooks/useCompanyIdentity";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { toCamelKeyObject } from "@/utils/case";
import { toPathPickProductKey } from "@/features/pathpick/utils";

// 일괄 주문 — 주문 수량 모달은 상품 전체 정보(옵션·재고·가격)가 필요해, 누르는 시점에 선택 상품을
// id__in으로 다시 조회해 넘긴다. 다른 페이지(검색 결과 전체 선택 포함)에서 고른 상품도 함께 주문된다.
// 액션 패널의 주문 행과 하단 선택 바가 같이 쓴다.
//
// 캐시에 남길 필요가 없는 1회성 조회라 query가 아니라 mutation으로 돌린다 — "api-products" 접두사 아래는
// 낙관적 업데이트·invalidate가 목록 응답으로 다루는 자리라 거기 섞이지 않게 하려는 이유도 있다.
// mutationKey를 공유해 한쪽에서 누른 동안 양쪽 버튼이 모두 로딩으로 막힌다.
const usePathPickBasketBulkOrder = () => {
  const { t } = useI18nHelper();
  const { companyId } = useCompanyIdentity();
  const { selectedProducts, selectedCount, openOrderModal } =
    usePathPickBasketSelection();
  const { error: toastError } = useAppToasts({ id: "GLOBAL" });

  const mutationKey = ["pathpick", "basket", "bulk-order", companyId];
  const isLoading = useIsMutating({ mutationKey }) > 0;

  const { mutate } = useMutation({
    mutationKey,
    mutationFn: async () => {
      // 선택 키("{type}:{id}")가 id__in 형식과 같아 그대로 보낸다.
      // 선택 순서(Map 삽입 순)를 모달 순서로 쓴다 — 응답 순서는 의미가 없다.
      const keys = Array.from(selectedProducts.keys());
      const data = await pathPickQueryApi.getPathPickProducts(
        companyId as number,
        {
          id__in: keys,
          // 담은 상품 기준으로 조회해야 그 사이 품절된 상품도 빠지지 않고 온다
          // (생략하면 재고 있는 상품만 내려와 모달에서 조용히 사라진다).
          isInBasket: true,
          pageNumber: 1,
          pageSize: keys.length,
        }
      );
      const { products } = toCamelKeyObject(data) as PathPickProductsResponse;
      const order = new Map(keys.map((key, index) => [key, index]));
      const rank = ({ productType, id }: PathPickProduct) =>
        order.get(toPathPickProductKey(productType, id)) ??
        Number.MAX_SAFE_INTEGER;
      return [...products].sort((a, b) => rank(a) - rank(b));
    },
    // 선택 이후 담기에서 빠진 상품은 응답에 없다 — 받은 상품만 모달에 띄운다.
    onSuccess: (products) => {
      if (products.length === 0) {
        toastError(t("path_pick:basket.order.load_failed"));
        return;
      }
      openOrderModal(products);
    },
    onError: () => toastError(t("path_pick:basket.order.load_failed")),
  });

  return {
    startBulkOrder: () => mutate(),
    isLoading,
    // 한도를 넘으면 요청 URL이 너무 길어져 주문 자체를 막는다(안내 툴팁 노출).
    isOverLimit: selectedCount > BASKET_BULK_ORDER_LIMIT,
  };
};

export default usePathPickBasketBulkOrder;
